import React, { useState, useMemo } from 'react';
import { Constants } from '@eagerdog/constants';
import Button from 'src/components/Button/Button';
import Checkbox from 'src/components/Checkbox/Checkbox';
import { apiService } from 'src/services/api.service';
import { toast } from 'src/components/Toast/ToastManager';
import styles from './RefundAll.module.scss';

interface RefundAllProps {
  transactions: any[];
  eventId: string;
  onFinish: () => void;
  onCancel: () => void;
}

interface RefundableTransaction {
  transaction: any;
  refundAmountDollars: number;
  refundAmountCents: number;
  currency: string;
}


const RefundAll: React.FC<RefundAllProps> = ({ transactions, eventId, onFinish, onCancel }) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState<{ succeeded: number; failed: number }>({ succeeded: 0, failed: 0 });
  const [includeStripeFees, setIncludeStripeFees] = useState(false);

  // ── Eligibility — mirrors Refund.tsx exactly ─────────────────────────────
  // Only process card transactions that are "success" and have a payment_intent_id.
  // Amount math matches the single Refund component:
  //   Without Stripe fees: totalCharge − stripeFee − alreadyRefunded
  //   With Stripe fees:    totalCharge − alreadyRefunded
  const { cardEligible, cardAlreadyRefunded, cardNoPaymentIntent, nonCard } = useMemo(() => {
    const cardEligible: RefundableTransaction[] = [];
    const cardAlreadyRefunded: any[] = [];  // had a charge but nothing left to refund
    const cardNoPaymentIntent: any[] = [];  // no settled Stripe charge — can't refund
    const nonCard: any[] = [];

    for (const tx of transactions) {
      const isCard = tx.amounts?.payment_method_type === Constants.payment_method_type.card;

      if (!isCard) {
        nonCard.push(tx);
        continue;
      }

      // Only auto-refund settled, successful card charges that have a payment intent
      if (tx.status !== 'success' || !tx.payment_intent_id) {
        cardNoPaymentIntent.push(tx);
        continue;
      }

      // Mirror Refund.tsx: totalCharge is the ceiling; ED fee reversal is handled by Stripe
      const totalChargeCents = tx.amounts?.calculated_total_amount || 0;
      const stripeFeeCents   = tx.amounts?.processed_processing_fee || tx.amounts?.calculated_processing_fee || 0;
      const alreadyRefunded  = tx.amounts?.refunded_amount || 0;

      const remainingWithStripe    = Math.max(0, totalChargeCents - alreadyRefunded);
      const remainingWithoutStripe = Math.max(0, totalChargeCents - stripeFeeCents - alreadyRefunded);
      const remainingCents = includeStripeFees ? remainingWithStripe : remainingWithoutStripe;

      if (remainingCents <= 0) {
        cardAlreadyRefunded.push(tx);
        continue;
      }

      const multiplier = tx.amounts?.amount_multiplier || 0.01;
      const refundAmountDollars = remainingCents * multiplier;

      cardEligible.push({
        transaction: tx,
        refundAmountDollars,
        refundAmountCents: remainingCents,
        currency: tx.amounts?.currency?.toUpperCase() || 'USD'
      });
    }

    return { cardEligible, cardAlreadyRefunded, cardNoPaymentIntent, nonCard };
  }, [transactions, includeStripeFees]);

  const totalCardRefundCents = cardEligible.reduce((sum, t) => sum + t.refundAmountCents, 0);
  const totalNonCardCents = nonCard.reduce((sum, tx) => {
    const processedTotal = tx.amounts?.processed_total_amount || tx.amounts?.calculated_total_amount || 0;
    const alreadyRefunded = tx.amounts?.refunded_amount || 0;
    return sum + Math.max(0, processedTotal - alreadyRefunded);
  }, 0);

  const primaryCurrency = transactions[0]?.amounts?.currency?.toUpperCase() || 'USD';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: primaryCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formatCents = (cents: number) => formatter.format(cents / 100);

  const handleConfirm = async () => {
    if (cardEligible.length === 0) return;

    setProcessing(true);
    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < cardEligible.length; i++) {
      const { transaction, refundAmountDollars } = cardEligible[i];
      try {
        await apiService.refundEventTransaction(eventId, transaction._id, {
          amount: refundAmountDollars
        });
        succeeded++;
      } catch (err) {
        console.error(`Refund failed for transaction ${transaction._id}:`, err);
        failed++;
      }
      setProgress(i + 1);
    }

    setResults({ succeeded, failed });
    setDone(true);
    setProcessing(false);

    if (failed === 0) {
      toast.show({
        title: 'Refund All Complete',
        content: `Successfully refunded ${succeeded} transaction${succeeded !== 1 ? 's' : ''}.`,
        type: 'success',
        duration: 6000
      });
    } else {
      toast.show({
        title: 'Refund All Partial',
        content: `${succeeded} succeeded, ${failed} failed. Check the transactions list.`,
        type: 'fail',
        duration: 8000
      });
    }

    onFinish();
  };

  if (done) return null;

  return (
    <div className={styles.refundAll}>
      <div className={styles.title}>Refund All Transactions</div>

      {!processing ? (
        <>
          <div className={styles.warning}>
            <span className={styles.warningIcon}>⚠️</span>
            <div>
              <strong>Card transactions only will be automatically processed.</strong>
              <p>Non-card transactions (cheque, cash, e-transfer, etc.) must be refunded manually by the club.</p>
              <p>Eager Dog fees will be credited back proportionally with each refund.</p>
            </div>
          </div>

          {(() => {
            const totalStripeFeeCents = transactions
              .filter(tx => tx.amounts?.payment_method_type === Constants.payment_method_type.card && tx.status === 'success' && tx.payment_intent_id)
              .reduce((sum: number, tx: any) => sum + (tx.amounts?.processed_processing_fee || tx.amounts?.calculated_processing_fee || 0), 0);
            return totalStripeFeeCents > 0 ? (
              <div className={styles.topOptions}>
                <Checkbox
                  id="includeStripeFees"
                  onChange={() => setIncludeStripeFees(v => !v)}
                  value={includeStripeFees}
                  label={`Include Stripe Fees (+${formatCents(totalStripeFeeCents)})`}
                />
                {includeStripeFees && (
                  <div className={styles.stripeFeeInfo}>
                    Stripe processing fees will be refunded from the club's Stripe account.
                  </div>
                )}
              </div>
            ) : null;
          })()}

          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <div className={styles.summarySection}>
                <div className={styles.sectionLabel}>Card Transactions to Refund</div>
                <div className={styles.sectionCount}>{cardEligible.length}</div>
                <div className={styles.sectionAmount}>{formatCents(totalCardRefundCents)}</div>
              </div>

              {nonCard.length > 0 && (
                <div className={`${styles.summarySection} ${styles.manualSection}`}>
                  <div className={styles.sectionLabel}>Non-Card (Club Handles Manually)</div>
                  <div className={styles.sectionCount}>{nonCard.length}</div>
                  <div className={styles.sectionAmount}>{formatCents(totalNonCardCents)}</div>
                </div>
              )}

              {cardAlreadyRefunded.length > 0 && (
                <div className={`${styles.summarySection} ${styles.skippedSection}`}>
                  <div className={styles.sectionLabel}>Already Refunded (Skipped)</div>
                  <div className={styles.sectionCount}>{cardAlreadyRefunded.length}</div>
                  <div className={styles.sectionAmount}></div>
                </div>
              )}

              {cardNoPaymentIntent.length > 0 && (
                <div className={`${styles.summarySection} ${styles.skippedSection}`}>
                  <div className={styles.sectionLabel}>No Settled Charge — Cannot Auto-Refund</div>
                  <div className={styles.sectionCount}>{cardNoPaymentIntent.length}</div>
                  <div className={styles.sectionAmount}></div>
                </div>
              )}
            </div>
          </div>

          {cardEligible.length === 0 && nonCard.length === 0 && cardNoPaymentIntent.length === 0 ? (
            <div className={styles.noEligible}>All transactions have already been refunded.</div>
          ) : cardEligible.length === 0 && cardNoPaymentIntent.length > 0 ? (
            <div className={styles.noEligible}>No card transactions can be auto-refunded — they have no settled Stripe charge on record. These may need to be handled manually.</div>
          ) : cardEligible.length === 0 ? (
            <div className={styles.noEligible}>No card transactions to auto-refund. Non-card transactions must be handled by the club.</div>
          ) : null}

          <div className={styles.actions}>
            <Button type="button" onClick={onCancel}>Cancel</Button>
            {cardEligible.length > 0 && (
              <Button type="button" onClick={handleConfirm}>
                Confirm
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className={styles.progressWrap}>
          <div className={styles.progressLabel}>
            Refunding {progress} of {cardEligible.length}…
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${Math.round((progress / cardEligible.length) * 100)}%` }}
            />
          </div>
          <div className={styles.progressSub}>Please do not close this window.</div>
        </div>
      )}
    </div>
  );
};

export default RefundAll;
